import { useCallback, useEffect, useMemo, useState } from "react";
import { Bell, MessageCircle, Send } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import {
  fetchMarketingReportConfig,
  saveMarketingReportConfig,
  sendMarketingReportTest,
  type MarketingReportRecipient,
} from "@/services/marketing";
import { cn } from "@/utils/cn";

interface Props {
  open: boolean;
  onClose: () => void;
}

function timeValue(hour: number, minute: number): string {
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function parseTime(value: string): { hour: number; minute: number } {
  const [h, m] = value.split(":").map(Number);
  return {
    hour: Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 17,
    minute: Number.isFinite(m) ? Math.min(59, Math.max(0, m)) : 0,
  };
}

export function MarketingReportModal({ open, onClose }: Props) {
  const [enabled, setEnabled] = useState(true);
  const [time, setTime] = useState("17:00");
  const [recipients, setRecipients] = useState<MarketingReportRecipient[]>([]);
  const [whatsappReady, setWhatsappReady] = useState(true);
  const [whatsappReason, setWhatsappReason] = useState<string | null>(null);
  const [lastSentDate, setLastSentDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<"self" | "all" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const selectedCount = useMemo(
    () => recipients.filter((row) => row.selected && row.hasPhone).length,
    [recipients],
  );
  const selectedWithoutPhone = useMemo(
    () => recipients.filter((row) => row.selected && !row.hasPhone).length,
    [recipients],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMarketingReportConfig();
      setEnabled(data.config.enabled);
      setTime(timeValue(data.config.hour, data.config.minute));
      setRecipients(data.recipients);
      setWhatsappReady(data.whatsappReady);
      setWhatsappReason(data.whatsappReason);
      setLastSentDate(data.config.lastSentDate);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar destinatários.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setNotice(null);
    void load();
  }, [open, load]);

  const toggleRecipient = (id: string) => {
    setRecipients((prev) =>
      prev.map((row) => (row.id === id ? { ...row, selected: !row.selected } : row)),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const parsed = parseTime(time);
      const data = await saveMarketingReportConfig({
        enabled,
        hour: parsed.hour,
        minute: parsed.minute,
        recipientUserIds: recipients.filter((row) => row.selected).map((row) => row.id),
      });
      setRecipients(data.recipients);
      setNotice("Configuração salva. O relatório sai todos os dias neste horário, com PDF no WhatsApp.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (toAll: boolean) => {
    setTesting(toAll ? "all" : "self");
    setError(null);
    setNotice(null);
    try {
      const parsed = parseTime(time);
      await saveMarketingReportConfig({
        enabled,
        hour: parsed.hour,
        minute: parsed.minute,
        recipientUserIds: recipients.filter((row) => row.selected).map((row) => row.id),
      });
      const result = await sendMarketingReportTest(
        toAll
          ? { userIds: recipients.filter((row) => row.selected).map((row) => row.id) }
          : { toAll: false },
      );
      const parts = [`Enviado para ${result.sent} pessoa(s).`];
      if (result.skipped > 0) parts.push(`${result.skipped} sem telefone.`);
      if (result.failed > 0) parts.push(`${result.failed} falha(s).`);
      setNotice(parts.join(" "));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao enviar teste.");
    } finally {
      setTesting(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-brand-600" />
          <span>Disparo do relatório no WhatsApp</span>
        </div>
      }
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          <Button variant="secondary" onClick={handleSave} loading={saving} disabled={loading}>
            Salvar
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="text-sm text-slate-600">
          Todo dia, no horário abaixo, o sistema envia as métricas, indicadores e o alcance das
          metas do mês, com o relatório em PDF no WhatsApp.
        </p>

        <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span>
            <span className="block text-sm font-semibold text-slate-800">Disparo automático ativo</span>
            <span className="text-xs text-slate-500">
              {lastSentDate
                ? `Último envio automático: ${lastSentDate.slice(8, 10)}/${lastSentDate.slice(5, 7)}/${lastSentDate.slice(0, 4)}`
                : "Ainda não houve envio automático."}
            </span>
          </span>
        </label>

        <label className="flex max-w-[180px] flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Horário (Brasília)
          </span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-800 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </label>

        {!whatsappReady && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {whatsappReason || "WhatsApp desconectado. Conecte a instância para enviar o relatório."}
          </p>
        )}

        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Quem recebe
            </p>
            <p className="text-xs text-slate-400">
              {selectedCount} com telefone
              {selectedWithoutPhone > 0 ? ` · ${selectedWithoutPhone} sem telefone` : ""}
            </p>
          </div>
          <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200">
            {loading ? (
              <p className="px-4 py-6 text-sm text-slate-500">Carregando pessoas...</p>
            ) : (
              recipients.map((row) => (
                <label
                  key={row.id}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 hover:bg-slate-50",
                    !row.hasPhone && "opacity-80",
                  )}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                    checked={row.selected}
                    onChange={() => toggleRecipient(row.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800">
                      {row.displayName}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {row.hasPhone ? row.phoneDisplay : "Sem telefone cadastrado"}
                      {row.role === "admin" ? " · admin" : ""}
                    </span>
                  </span>
                </label>
              ))
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Send className="h-4 w-4" />}
            loading={testing === "self"}
            disabled={loading || testing !== null}
            onClick={() => void handleTest(false)}
          >
            Enviar teste para mim
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Bell className="h-4 w-4" />}
            loading={testing === "all"}
            disabled={loading || testing !== null || selectedCount === 0}
            onClick={() => void handleTest(true)}
          >
            Enviar teste para os selecionados
          </Button>
        </div>

        {notice && <p className="text-sm font-medium text-emerald-700">{notice}</p>}
        {error && <p className="text-sm font-medium text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
